import React, {useMemo} from 'react'

import {
  AttendanceResultSection,
  AttendanceV2Payload,
  getResultSections,
} from '../../../../modes/attendance/attendanceV2Types'
import AttendanceV2ResultView from './AttendanceV2ResultView'

type AttendanceV2SectionedResultViewProps = {
  payload: AttendanceV2Payload
  theme: 'light' | 'dark'
  tableUnavailableHint?: boolean
}

/** Carry one section's own result set into a standalone payload so every existing reader — table,
 *  visual summary, export — works on it exactly as on a single-result answer. The parent's merged
 *  `rows`/`stats` and the `sections` array itself are intentionally dropped; the section's own
 *  `stats` (including `noDataFor`) rides along. */
function sectionToPayload(
  payload: AttendanceV2Payload,
  section: AttendanceResultSection
): AttendanceV2Payload {
  return {
    ...payload,
    data: {
      rows: section.rows ?? [],
      grouped: section.grouped,
      stats: section.stats ?? null,
      // The section's own window and mark unit. Without them a section exported on its own falls
      // back to the parent's figures, or prints no window at all.
      period: section.period ?? payload.data?.period ?? null,
      markType: section.markType ?? payload.data?.markType ?? null,
    },
  }
}

/** Renders `data.sections` — a fanned-out answer's labeled result sets — one card per section, each
 *  with its own stats, table, and (when that set failed) error. Callers fall back to a single
 *  `AttendanceV2ResultView` when the answer isn't sectioned. */
const AttendanceV2SectionedResultView: React.FC<AttendanceV2SectionedResultViewProps> = ({
  payload,
  theme,
  tableUnavailableHint = false,
}) => {
  const sections = useMemo(() => getResultSections(payload), [payload])
  const sectionPayloads = useMemo(
    () => sections.map((section) => sectionToPayload(payload, section)),
    [payload, sections]
  )

  if (!sections.length) return null

  return (
    <>
      {sections.map((section, index) => (
        <AttendanceV2ResultView
          key={`${section.label ?? 'section'}-${index}`}
          payload={sectionPayloads[index]}
          theme={theme}
          tableUnavailableHint={tableUnavailableHint}
          label={section.label ?? undefined}
          sectionError={section.error ?? undefined}
        />
      ))}
    </>
  )
}

export default AttendanceV2SectionedResultView
